import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import {
  Field,
  Radio,
  RadioGroup,
  FluentProvider,
  webLightTheme,
  Textarea,
} from '@fluentui/react-components';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { useParams } from 'react-router-dom';
import ActionButtons from '../../common/ActionButtons';
import UtcDatePicker from '../../common/utcDatePicker/UtcDatePicker';
import { UPDATE_XBRL_MUTATION } from '../../../utils/statApi/AnnualFilingRequirementsApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import IAnnualFilingRequirements from '../../../utils/types/IAnnualFilingRequirements';
import { ICompanyCodeParams } from '../../common/ParamTypes';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import { yesOption, noOption } from '../../../utils/types/CommonDropdownOptions';

interface IXBRLForm {
  xbrlDetails: IAnnualFilingRequirements;
  closePanel: () => void;
}

const XBRLForm = ({ xbrlDetails, closePanel }: IXBRLForm): JSX.Element => {
  const [pageState, setPageState] = useState({ ...xbrlDetails });
  const { companyCode } = useParams<ICompanyCodeParams>();
  const [firstXBRLDate, setfirstXBRLDate] = useState(
    xbrlDetails?.dateOfFirstXbrlFilingRequirement?.toString(),
  );
  const [updateXBRLDetails, { loading, error }] = useMutation(UPDATE_XBRL_MUTATION, {
    onCompleted: () => {
      closePanel();
    },
  });

  const onTaggedSectionChange = (newValue?: string): void => {
    setPageState({ ...pageState, sectionOfFilingRequiredToBeTagged: newValue });
  };

  const onCommentsChange = (newValue?: string): void => {
    setPageState({ ...pageState, xbrlComments: newValue });
  };

  const onRequiredFilingChange = (newValue?: string): void => {
    setPageState({ ...pageState, filingsRequiredToBeFiledInXbrl: newValue });
  };

  const saveXBRL = (): void => {
    const payload = {
      id: pageState.id,
      xbrlRequiredForFilings: pageState.xbrlRequiredForFilings,
      filingsRequiredToBeFiledInXbrl: pageState.filingsRequiredToBeFiledInXbrl,
      dateOfFirstXbrlFilingRequirement: new Date(firstXBRLDate),
      sectionOfFilingRequiredToBeTagged: pageState.sectionOfFilingRequiredToBeTagged,
      xbrlComments: pageState.xbrlComments,
    };

    updateXBRLDetails({
      variables: {
        companyCode,
        xbrlData: payload,
      },
    });
  };

  return (
    <CoherencePanel
      closeButtonAriaLabel="Close XBRL Edit Panel"
      panelSize={CoherencePanelSize.medium}
      titleText="Edit XBRL Form"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          handleSubmit={saveXBRL}
          saveLabel="Save XBRL Form"
          saveTitle="Save XBRL Form"
          cancelLabel="Cancel XBRL Form Edit"
          cancelTitle="Cancel XBRL Form Edit"
          mutationLoading={loading}
        />
      )}
    >
      <form>
        <FluentProvider theme={webLightTheme}>
          <Stack>
            <LoadingErrorMessage error={error} loading={loading} />
            <Field label="XBRL Required for Statutory Filings">
              <RadioGroup
                id="xbrlRequiredForStatutoryFilings"
                name="xbrlRequiredForStatutoryFilings"
                value={pageState.xbrlRequiredForFilings?.toString()}
                onChange={(_, data) => {
                  setPageState({ ...pageState, xbrlRequiredForFilings: data.value === 'true' });
                }}
              >
                <Radio value={yesOption.key} label={yesOption.text} />
                <Radio value={noOption.key} label={noOption.text} />
              </RadioGroup>
            </Field>
            <br />
            <Field label="Section of Filing Required to be Tagged">
              <Textarea
                aria-label="Section of Filing Required to be Tagged"
                value={pageState?.sectionOfFilingRequiredToBeTagged}
                onChange={(e, data) => onTaggedSectionChange(data.value)}
                resize="none"
              />
            </Field>
            <br />
            <Field label="Filings Required to be Filed in XBRL">
              <Textarea
                aria-label="Filings Required to be Filed in XBRL"
                value={pageState?.filingsRequiredToBeFiledInXbrl}
                onChange={(e, data) => onRequiredFilingChange(data.value)}
                resize="none"
              />
            </Field>
            <br />
            <Field label="XBRL Comments">
              <Textarea
                aria-label="XBRL Comments"
                value={pageState?.xbrlComments}
                onChange={(e, data) => onCommentsChange(data.value)}
                resize="none"
              />
            </Field>
            <br />
            <UtcDatePicker
              label="Date of First XBRL Filing Requirement"
              textField={{
                name: 'firstXBRLDate',
              }}
              value={firstXBRLDate}
              onSelectDate={setfirstXBRLDate}
            />
          </Stack>
        </FluentProvider>
      </form>
    </CoherencePanel>
  );
};

export default XBRLForm;
